import React from 'react';
import { RouteProp } from '@react-navigation/native';
import { getIntl } from '../../../../../utils/intl';
import useMedicalProfiles from '../../../../../hooks/api/useMedicalProfiles';
import useCareTeamMember from '../../../../../hooks/api/useCareTeamMember';
import { ProfilesTabParamList, useGoBack, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import {
    useHasPermissionAcrossProfiles,
    useIsLoadingPermissionsAcrossProfiles,
} from '../../../../v2BaseComponents/Permissions/hooks';
import getName from '../../../../../utils/getName';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import TitledSection from '../../../../v2BaseComponents/TitledSection';
import Select from '../../../../v2BaseComponents/Inputs/Select';
import { SelectOption } from '../../../../v2BaseComponents/Inputs/Select/types';
import HeaderContainer from '../../../../v2BaseComponents/HeaderContainer';
import CareTeamMemberHeader from '../../../../shared/CareTeam/CareTeamMemberHeader';

const intl = getIntl('copyCareTeamMember', {
    header: 'Copy to Profile',
    selectTitle: 'Destination Medical Profile',
    noProfilesError: 'Add a Medical Profile to copy this care team member',
    duplicate: 'Duplicate Care Team Member',
});

export type CopyCareTeamMemberScreenOwnProps = {};

export type CopyCareTeamMemberScreenProps = CopyCareTeamMemberScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'copy-care-team-member'>;
};

const CopyCareTeamMemberScreen: React.FC<CopyCareTeamMemberScreenProps> = (props) => {
    const { profileId: sourceProfileId, careTeamMemberId: sourceCareTeamMemberId } = props.route.params;
    const { medicalProfiles, isLoadingProfiles, collaboratorMedicalProfiles } = useMedicalProfiles();
    const navigator = useProfilesTabNavigation();
    const [selectedDestinationProfileId, setSelectedDestinationProfileId] = React.useState<string | null>(null);
    const goBack = useGoBack();

    const allMedicalProfiles = React.useMemo(() => {
        return [...medicalProfiles, ...collaboratorMedicalProfiles];
    }, [medicalProfiles, collaboratorMedicalProfiles]);
    const allowedProfileIds = useHasPermissionAcrossProfiles(
        allMedicalProfiles.map((_) => _.medicalProfileId),
        'careTeam',
        'edit',
    );
    const allowedMedicalProfiles = React.useMemo(() => {
        return allMedicalProfiles.filter(
            (_) => allowedProfileIds.includes(_.medicalProfileId) && _.medicalProfileId !== sourceProfileId,
        );
    }, [allMedicalProfiles, allowedProfileIds, sourceProfileId]);
    const isLoadingPermissions = useIsLoadingPermissionsAcrossProfiles(
        allMedicalProfiles.map((_) => _.medicalProfileId),
    );

    const { copyCareTeamMember, isCopyingCareTeamMember, careTeamMember, isLoadingCareTeamMember } = useCareTeamMember({
        medicalProfileId: sourceProfileId,
        careTeamMemberId: sourceCareTeamMemberId,
        onCopyCareTeamMemberSuccess: (destinationCareTeamMemberId: string) => {
            if (!selectedDestinationProfileId) {
                goBack();
                return;
            }
            navigator.navigate('care-team-member', {
                profileId: selectedDestinationProfileId,
                careTeamMemberId: destinationCareTeamMemberId,
            });
        },
    });

    const options: SelectOption[] = allowedMedicalProfiles
        .map((_) => ({
            label: getName(_),
            value: _.medicalProfileId,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    return (
        <AnimateLoadedContent
            isLoading={isCopyingCareTeamMember || isLoadingPermissions || isLoadingProfiles || isLoadingCareTeamMember}
        >
            {() => (
                <>
                    <HeaderContainer>
                        <CareTeamMemberHeader
                            careTeamMemberId={sourceCareTeamMemberId}
                            source={{
                                source: 'profile',
                                medicalProfileId: sourceProfileId,
                            }}
                            careTeamMember={careTeamMember!}
                        />
                    </HeaderContainer>
                    <ActionButtonLayout
                        scrollable
                        whiteBackground
                        actions={[
                            {
                                title: intl('duplicate'),
                                icon: 'Copy',
                                onPress: () => {
                                    if (selectedDestinationProfileId) copyCareTeamMember(selectedDestinationProfileId);
                                },
                            },
                        ]}
                    >
                        <MainContentContainer>
                            <TitledSection title={intl('header')}>
                                <Select
                                    title={intl('selectTitle')}
                                    selectOptions={options}
                                    value={selectedDestinationProfileId}
                                    onChange={setSelectedDestinationProfileId}
                                    isDisabled={!options.length}
                                    error={!options.length ? intl('noProfilesError') : undefined}
                                />
                            </TitledSection>
                        </MainContentContainer>
                    </ActionButtonLayout>
                </>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(CopyCareTeamMemberScreen);
